import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Stack,
  LinearProgress,
  Alert,
} from '@mui/material';
import { ref, uploadBytesResumable, getDownloadURL, getStorage } from 'firebase/storage';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CancelIcon from '@mui/icons-material/Cancel';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

const ACCEPTED_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
];
const ACCEPT_STRING = '.pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/png,image/jpeg,image/jpg,image/webp';

interface FileUploadProgress {
  file: File;
  progress: number;
  url?: string;
  error?: string;
}

interface KhaataBillUploadProps {
  onFileUpload: (url: string) => void;
  onFileRemove: () => void;
  /** Called when a bill upload starts or finishes (including retry). */
  onUploadingChange?: (uploading: boolean) => void;
  restaurantId: string;
  /** Expense ID (for edit) or user ID (for new expense) - used in file name */
  expenseOrUserId: string;
  existingUrl?: string;
  disabled?: boolean;
  maxFileSizeMB?: number;
}

export default function KhaataBillUpload({
  onFileUpload,
  onFileRemove,
  onUploadingChange,
  restaurantId,
  expenseOrUserId,
  existingUrl,
  disabled,
  maxFileSizeMB = 5,
}: KhaataBillUploadProps) {
  const [uploadProgress, setUploadProgress] = useState<FileUploadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const storage = getStorage();

  const hasExisting = !!existingUrl;
  const isUploading = !!uploadProgress && uploadProgress.progress < 100 && !uploadProgress.error;
  const isAtMax = hasExisting || (uploadProgress?.url !== null && uploadProgress?.url !== undefined);
  const disableUploadControl = disabled || isUploading || isAtMax;

  useEffect(() => {
    onUploadingChange?.(isUploading);
  }, [isUploading, onUploadingChange]);

  const buildObjectPath = (file: File) => {
    const originalName = file.name || 'bill';
    const dotIndex = originalName.lastIndexOf('.');
    const extension = dotIndex > -1 ? originalName.substring(dotIndex).toLowerCase() : '';

    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthLabel = monthNames[now.getMonth()];
    const year = now.getFullYear();
    const dateFolder = `${day}-${monthLabel}-${year}`;

    const safeId = (expenseOrUserId || 'noid').replace(/[^a-zA-Z0-9-_]/g, '_');
    const timestamp = Date.now();
    const finalName = `${safeId}-${timestamp}${extension}`;

    return `khaata-bills/${restaurantId}/${dateFolder}/${finalName}`;
  };

  const uploadFile = (file: File, options?: { reuseEntry?: boolean }) => {
    const storageRef = ref(storage, buildObjectPath(file));
    const uploadTask = uploadBytesResumable(storageRef, file);

    if (!options?.reuseEntry) {
      setUploadProgress({ file, progress: 0 });
    } else if (uploadProgress) {
      setUploadProgress({
        ...uploadProgress,
        progress: 0,
        error: undefined,
        url: undefined,
      });
    }

    return new Promise<string>((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress((prev) =>
            prev?.file === file ? { ...prev, progress } : prev,
          );
        },
        (err) => {
          console.error('Upload error:', err);
          setUploadProgress((prev) =>
            prev?.file === file
              ? { ...prev, error: 'Failed to upload file. Please try again.' }
              : prev,
          );
          reject(err);
        },
        async () => {
          try {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            setUploadProgress((prev) =>
              prev?.file === file ? { ...prev, progress: 100, url } : prev,
            );
            resolve(url);
          } catch (err) {
            console.error('Failed to get download URL:', err);
            reject(err);
          }
        },
      );
    });
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;

    if (isAtMax) {
      setError('Maximum 1 bill allowed. Remove the existing bill before uploading a new one.');
      e.target.value = '';
      return;
    }

    const file = e.target.files[0];
    const maxBytes = maxFileSizeMB * 1024 * 1024;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError(
        `Invalid file type. Allowed: PDF, PNG, JPG, WEBP. Got: ${file.name}`,
      );
      e.target.value = '';
      return;
    }

    if (file.size > maxBytes) {
      setError(`${file.name}: File is larger than ${maxFileSizeMB}MB.`);
      e.target.value = '';
      return;
    }

    setError(null);

    try {
      const url = await uploadFile(file);
      onFileUpload(url);
      setUploadProgress(null);
    } catch (err) {
      setError('Failed to upload bill. Please try again.');
      console.error('Upload error:', err);
    } finally {
      e.target.value = '';
    }
  };

  const handleRemoveFile = (url: string) => {
    if (url === existingUrl) {
      onFileRemove();
    } else if (uploadProgress?.url === url) {
      onFileRemove();
      setUploadProgress(null);
    }
  };

  const handleRetryUpload = (file: File) => {
    setError(null);
    uploadFile(file, { reuseEntry: true })
      .then((url) => onFileUpload(url))
      .catch(() => {
        setError('Failed to upload bill. Please try again.');
      });
  };

  const renderPreview = (url: string, fileName?: string) => {
    const isPdf = url.toLowerCase().includes('.pdf') || fileName?.toLowerCase().endsWith('.pdf');
    const nameFromUrl = decodeURIComponent(url.split('/').pop() || '').split('?')[0];

    return (
      <Box
        sx={{
          width: { xs: '100%', sm: 160 },
          borderRadius: 2,
          bgcolor: '#fff',
          boxShadow: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ p: 1 }}>
          <Typography variant="caption" noWrap title={fileName || nameFromUrl}>
            {fileName || nameFromUrl}
          </Typography>
        </Box>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            pt: '75%',
            overflow: 'hidden',
            bgcolor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isPdf ? (
            <PictureAsPdfIcon sx={{ fontSize: 48, color: 'error.main' }} />
          ) : (
            <Box
              component="img"
              src={url}
              alt={nameFromUrl}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          )}
        </Box>
        <Button
          size="small"
          startIcon={<CancelIcon />}
          onClick={() => handleRemoveFile(url)}
          sx={{ borderRadius: 0 }}
        >
          Remove
        </Button>
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
        }}
      >
        <Box>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Attach Bill
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Max 1 bill (PDF, PNG, JPG, WEBP). Max size {maxFileSizeMB}MB.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          component="label"
          startIcon={<FileUploadIcon />}
          disabled={disableUploadControl}
          size="small"
          sx={{ width: { xs: '100%', sm: 'auto' }, mt: { xs: 1, sm: 0 } }}
        >
          {isUploading ? 'Uploading…' : isAtMax ? 'Bill attached' : 'Upload Bill'}
          <input
            type="file"
            hidden
            accept={ACCEPT_STRING}
            disabled={disableUploadControl}
            onChange={handleFileUpload}
          />
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={2} direction="row" flexWrap="wrap" sx={{ mt: 1 }}>
        {existingUrl && renderPreview(existingUrl)}
        {uploadProgress && (
          <Box
            sx={{
              width: { xs: '100%', sm: 160 },
              borderRadius: 2,
              bgcolor: '#fff',
              boxShadow: 1,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ p: 1 }}>
              <Typography variant="caption" noWrap title={uploadProgress.file.name}>
                {uploadProgress.file.name}
              </Typography>
              {uploadProgress.error ? (
                <>
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {uploadProgress.error}
                  </Alert>
                  <Button
                    size="small"
                    onClick={() => handleRetryUpload(uploadProgress.file)}
                    sx={{ mt: 1 }}
                  >
                    Re-upload
                  </Button>
                </>
              ) : (
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress.progress}
                  sx={{ height: 6, borderRadius: 999, mt: 1 }}
                />
              )}
            </Box>
            {uploadProgress.file.type.startsWith('image/') && (
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  pt: '75%',
                  overflow: 'hidden',
                  bgcolor: 'grey.100',
                }}
              >
                <Box
                  component="img"
                  src={URL.createObjectURL(uploadProgress.file)}
                  alt={uploadProgress.file.name}
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            )}
            {uploadProgress.file.type === 'application/pdf' && (
              <Box
                sx={{
                  width: '100%',
                  pt: '75%',
                  bgcolor: 'grey.100',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PictureAsPdfIcon sx={{ fontSize: 48, color: 'error.main' }} />
              </Box>
            )}
            {uploadProgress.url && (
              <Button
                size="small"
                startIcon={<CancelIcon />}
                onClick={() => handleRemoveFile(uploadProgress.url!)}
                sx={{ borderRadius: 0 }}
              >
                Remove
              </Button>
            )}
          </Box>
        )}
      </Stack>
    </Box>
  );
}
